import React from 'react';
import { Button as MuiButton, CircularProgress, makeStyles } from '@material-ui/core';
import { designTokens } from '../../styles/designTokens';

const useStyles = makeStyles({
    button: {
        width: '100%',
        boxShadow: 'none',
        '&:hover': {
            boxShadow: 'none',
        },
    },
    primary: {
        backgroundColor: designTokens.colors.primary,
        color: designTokens.colors.text.white,
        border: `1px solid ${designTokens.colors.primary}`,
        borderRadius: designTokens.borderRadius.button,
        '& span': {
            ...designTokens.typography.button,
        },
        '&:hover': {
            backgroundColor: designTokens.colors.primaryDark,
            borderColor: designTokens.colors.primaryDark,
        },
        '&:disabled': {
            backgroundColor: designTokens.colors.primaryLight,
            borderColor: designTokens.colors.primaryLight,
            color: designTokens.colors.text.white,
        }
    },
    secondary: {
        backgroundColor: designTokens.colors.background.card,
        color: designTokens.colors.primary,
        border: `1px solid ${designTokens.colors.primary}`,
        borderRadius: designTokens.borderRadius.button,
        '& span': {
            ...designTokens.typography.button,
        },
        '&:hover': {
            backgroundColor: designTokens.colors.grey[100],
        },
        '&:disabled': {
            color: designTokens.colors.text.disabled,
            borderColor: designTokens.colors.primaryLight,
        }
    },
    outlined: {
        backgroundColor: 'transparent',
        color: designTokens.colors.primary,
        border: `1px solid ${designTokens.colors.primary}`,
        borderRadius: designTokens.borderRadius.button,
        '& span': {
            ...designTokens.typography.button,
        },
        '&:hover': {
            backgroundColor: designTokens.colors.grey[100],
        },
        '&:disabled': {
            color: designTokens.colors.text.disabled,
            borderColor: designTokens.colors.primaryLight,
        }
    },
    danger: {
        backgroundColor: 'transparent',
        color: designTokens.colors.red.main,
        border: `1px solid ${designTokens.colors.red.main}`,
        borderRadius: designTokens.borderRadius.button,
        '& span': {
            ...designTokens.typography.button,
        },
        '&:hover': {
            backgroundColor: designTokens.colors.red.light,
            borderColor: designTokens.colors.red.dark,
            color: designTokens.colors.red.dark,
        },
        '&:disabled': {
            color: designTokens.colors.text.disabled,
            borderColor: designTokens.colors.grey[300],
        }
    },
    dangerFilled: {
        backgroundColor: designTokens.colors.red.main,
        color: designTokens.colors.text.white,
        border: `1px solid ${designTokens.colors.red.main}`,
        borderRadius: designTokens.borderRadius.button,
        '& span': {
            ...designTokens.typography.button,
        },
        '&:hover': {
            backgroundColor: designTokens.colors.red.dark,
            borderColor: designTokens.colors.red.dark,
        },
        '&:disabled': {
            backgroundColor: designTokens.colors.red.light,
            borderColor: designTokens.colors.red.light,
            color: designTokens.colors.text.white,
        }
    },
    iconOnly: {
        padding: designTokens.spacing.md,
        minWidth: 'auto',
    },
    iconLeft: {
        marginRight: designTokens.spacing.sm,
        display: 'inline-flex',
        alignItems: 'center',
        '& svg': {
            fontSize: '20px',
        }
    },
    iconRight: {
        marginLeft: designTokens.spacing.sm,
        display: 'inline-flex',
        alignItems: 'center',
        '& svg': {
            fontSize: '20px',
        }
    },
    loader: {
        marginRight: designTokens.spacing.sm,
    },
    loaderPrimary: {
        color: designTokens.colors.background.card,
    },
    loaderSecondary: {
        color: designTokens.colors.primary,
    },
    loaderDanger: {
        color: designTokens.colors.red.main,
    },
    default: {
        padding: '20px 30px',
    },
    small: {
        padding: '10px 20px',
    },
});

const Button = ({
    variant = 'primary',
    children,
    onClick,
    disabled = false,
    loading = false,
    icon,
    iconPosition = 'left',
    className,
    size = 'default',
    ...rest
}) => {
    const classes = useStyles();

    const handleClick = (e) => {
        if (!loading && !disabled && onClick) {
            onClick(e);
        }
    };

    const isIconOnly = icon && !children;

    const loaderClass =
        variant === 'primary' || variant === 'dangerFilled' ? classes.loaderPrimary :
        variant === 'danger' ? classes.loaderDanger :
        classes.loaderSecondary;

    const buttonClasses = [
        classes.button,
        classes[variant],
        isIconOnly && classes.iconOnly,
        classes[size],
        className
    ].filter(Boolean).join(' ');

    return (
        <MuiButton
            className={buttonClasses}
            onClick={handleClick}
            disabled={disabled || loading}
            disableElevation
            {...rest}
        >
            {loading && (
                <CircularProgress
                    size={16}
                    className={`${classes.loader} ${loaderClass}`}
                />
            )}

            {!loading && icon && iconPosition === 'left' && (
                <span className={classes.iconLeft}>{icon}</span>
            )}

            {children}

            {!loading && icon && iconPosition === 'right' && (
                <span className={classes.iconRight}>{icon}</span>
            )}
        </MuiButton>
    );
};

export default Button;
